import React, { useEffect, CSSProperties } from "react";

export interface LoaderOverlayProps {
	isOpen: boolean;
	message?: string;
	loaderPosition?:
		"center" | "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
	backdropColorClass?: string;
	backdropOpacityClass?: string;
	loaderColorClass?: string;
	textColorClass?: string;
	textMessageClasses?: string;
	zIndex?: string;
	positioning?: "fixed" | "absolute";
	style?: CSSProperties;
	className?: string;
	contentClassName?: string;
}

const LoaderOverlay: React.FC<LoaderOverlayProps> = ({
	isOpen,
	message,
	loaderPosition = "center",
	backdropColorClass = "bg-black",
	backdropOpacityClass = "opacity-50",
	loaderColorClass = "border-blue-500",
	textColorClass = "text-white",
	textMessageClasses = "text-md text-center font-semibold",
	zIndex = "z-70",
	positioning = "fixed",
	style,
	className,
	contentClassName,
}) => {
	useEffect(() => {
		if (positioning === "fixed") {
			const originalOverflow = document.body.style.overflow;
			if (isOpen) {
				document.body.style.overflow = "hidden";
			} else {
				document.body.style.overflow = originalOverflow;
			}
			return () => {
				if (document.body.style.overflow === "hidden" && isOpen) {
					document.body.style.overflow = originalOverflow;
				} else if (!isOpen && document.body.style.overflow === "hidden" && originalOverflow !== "hidden") {
					document.body.style.overflow = originalOverflow === "hidden" && !isOpen ? "hidden" : "unset";
				}
			};
		}
	}, [isOpen, positioning]);

	if (!isOpen) {
		return null;
	}

	let positionLayoutClasses = "items-center justify-center";
	switch (loaderPosition) {
		case "top":
			positionLayoutClasses = "items-start justify-center pt-10";
			break;
		case "bottom":
			positionLayoutClasses = "items-end justify-center pb-10";
			break;
		case "left":
			positionLayoutClasses = "items-center justify-start pl-10";
			break;
		case "right":
			positionLayoutClasses = "items-center justify-end pr-10";
			break;
		case "top-left":
			positionLayoutClasses = "items-start justify-start pt-10 pl-10";
			break;
		case "top-right":
			positionLayoutClasses = "items-start justify-end pt-10 pr-10";
			break;
		case "bottom-left":
			positionLayoutClasses = "items-end justify-start pb-10 pl-10";
			break;
		case "bottom-right":
			positionLayoutClasses = "items-end justify-end pb-10 pr-10";
			break;
	}

	const basePositioningClass = positioning === "absolute" ? "absolute" : "fixed";

	const hasOpacityInColorClass = backdropColorClass && backdropColorClass.includes("/[");
	const finalBackdropOpacityClass = hasOpacityInColorClass ? "" : backdropOpacityClass;

	return (
		<div
			className={`app-overlay ${basePositioningClass} inset-0 ${backdropColorClass} ${finalBackdropOpacityClass} flex ${positionLayoutClasses} ${zIndex} transition-opacity duration-300 ease-in-out ${className || ""}`}
			style={style}
			role="status"
			aria-live="polite"
			aria-busy={isOpen}
		>
			<div
				className={`flex flex-col items-center space-y-3 rounded-xl bg-gray-800 p-6 opacity-75 shadow-2xl ${contentClassName || ""}`}
			>
				{message && <p className={`${textMessageClasses} ${textColorClass}`}>{message}</p>}
				<div
					className={`h-10 w-10 border-4 ${loaderColorClass} animate-spin rounded-full border-solid`}
					style={{ borderTopColor: "transparent" }}
					aria-label="Loading"
				></div>
			</div>
		</div>
	);
};

export default React.memo(LoaderOverlay);
